<template>
  <div class="active-filters">
    <div
      class="active-filter"
      v-if="filters?.cityId && showFilter('location')"
      v-for="city in cities"
      :key="`city-${city.cityId}`"
    >
      <span class="filter-value">{{ city.city }}</span>
      <button
        class="filter-delete icon-close"
        v-if="showClose"
        @click="deleteFilterValue('cityId', city.cityId)"
        :aria-label="`${city.city}, poista`"
      ></button>
    </div>
    <div
      class="active-filter"
      v-if="getFilter('countyId') && showFilter('location')"
      v-for="county in counties"
      :key="`county-${county.countyId}`"
    >
      <span class="filter-value">{{ county.county }}</span>
      <button
        v-if="showClose"
        class="filter-delete icon-close"
        @click="deleteFilterValue('countyId', county.countyId)"
        :aria-label="`${county.county}, poista`"
      ></button>
    </div>
    <div class="active-filter" v-if="getFilterRange('price') && showFilter('price')">
      <span class="filter-value">{{ getRangeLabelText(getFilterRange('price'), '€') }}</span>
      <button
        class="filter-delete icon-close"
        v-if="showClose"
        @click="resetFilterRange('price')"
        :aria-label="`${getRangeLabelText(getFilterRange('price'), '€')}, poista`"
      ></button>
    </div>
    <div class="active-filter" v-if="getFilterRange('livingArea') && showFilter('livingArea')">
      <span class="filter-value" v-html="getAreaLabelText(getFilterRange('livingArea'))"></span>
      <button
        class="filter-delete icon-close"
        v-if="showClose"
        @click="resetFilterRange('livingArea')"
        :aria-label="`${getAreaLabelTextPlain(getFilterRange('livingArea'))}, poista`"
      ></button>
    </div>
    <div
      class="active-filter"
      v-if="getFilter('streetId') && showFilter('location')"
      v-for="street in streets"
      :key="`street-${street.streetId}`"
    >
      <span class="filter-value">{{ street.street }}, {{ street.city }}</span>
      <button
        v-if="showClose"
        class="filter-delete icon-close"
        @click="deleteFilterValue('streetId', street.streetId)"
        :aria-label="`${street.street}, ${street.city}, poista`"
      ></button>
    </div>
    <div
      class="active-filter"
      v-if="getFilter('districtId') && showFilter('location')"
      v-for="district in districts"
      :key="`district-${district.districtId}`"
    >
      <span class="filter-value">{{ district.district }}, {{ district.city }}</span>
      <button
        v-if="showClose"
        class="filter-delete icon-close"
        @click="deleteFilterValue('districtId', district.districtId)"
        :aria-label="` ${district.district}, ${district.city}, poista`"
      ></button>
    </div>
    <div
      class="active-filter"
      v-if="getFilter('regionId') && showFilter('location')"
      v-for="region in regions"
      :key="`region-${region.regionId}`"
    >
      <span class="filter-value">{{ region.region }}, {{ region.city }}</span>
      <button
        v-if="showClose"
        class="filter-delete icon-close"
        @click="deleteFilterValue('regionId', region.regionId)"
        :aria-label="`${region.region}, ${region.city}, poista`"
      ></button>
    </div>
    <div
      class="active-filter"
      v-if="getPostalCodes && showFilter('location')"
      v-for="(code, index) in getPostalCodes"
      :key="`postalcode-${index}`"
    >
      <span class="filter-value">{{ code }}</span>
      <button
        class="filter-delete icon-close"
        v-if="showClose"
        @click="deleteFilterValue('postalCode', code)"
        :aria-label="`${code}, poista`"
      ></button>
    </div>
    <div class="active-filter" v-if="getFilterRange('rent') && showFilter('rent')">
      <span class="filter-value">{{ getRangeLabelText(getFilterRange('rent'), '€/kk') }}</span>
      <button
        class="filter-delete icon-close"
        v-if="showClose"
        @click="resetFilterRange('rent')"
        :aria-label="`${getRangeLabelText(getFilterRange('rent'), '€/kk')}, poista`"
      ></button>
    </div>
    <div class="active-filter" v-if="getFilterRange('siteArea') && showFilter('siteArea')">
      <span class="filter-value" v-html="getAreaLabelText(getFilterRange('siteArea'))"></span>
      <button
        class="filter-delete icon-close"
        v-if="showClose"
        @click="resetFilterRange('siteArea')"
        :aria-label="`${getAreaLabelTextPlain(getFilterRange('siteArea'))}, poista`"
      ></button>
    </div>
    <div class="active-filter" v-if="getFilterRange('totalArea') && showFilter('totalArea')">
      <span class="filter-value" v-html="getAreaLabelText(getFilterRange('totalArea'))"></span>
      <button
        class="filter-delete icon-close"
        v-if="showClose"
        @click="resetFilterRange('totalArea')"
        :aria-label="`${getAreaLabelTextPlain(getFilterRange('totalArea'))}, poista`"
      ></button>
    </div>
    <div
      class="active-filter"
      v-if="getFilter('numberOfRooms') && showFilter('numberOfRooms')"
      v-for="(room, index) in getFilter('numberOfRooms')"
      :key="`number-of-rooms-${index}`"
    >
      <span class="filter-value">
        {{ room }}{{ parseInt(room, 10) === 5 ? '+' : '' }}
        {{ parseInt(room, 10) === 1 ? 'huone' : 'huonetta' }}
      </span>
      <button
        class="filter-delete icon-close"
        v-if="showClose"
        @click="deleteFilterValue('numberOfRooms', room)"
        :aria-label="`${room}${parseInt(room, 10) === 5 ? '+' : ''} ${parseInt(room, 10) === 1 ? 'huone' : 'huonetta'}, poista`"
      ></button>
    </div>
    <div
      class="active-filter"
      v-if="getFilter('condition') && showFilter('condition')"
      v-for="conditionItem in selectedConditions"
      :key="`condition-${conditionItem.id}`"
    >
      <span class="filter-value">{{ conditionItem.name }}</span>
      <button
        v-if="showClose"
        class="filter-delete icon-close"
        @click="deleteFilterValue('condition', conditionItem.id)"
        :aria-label="`${conditionItem.name}, poista`"
      ></button>
    </div>
    <div
      class="active-filter"
      v-if="getFilter('type') && showFilter('type')"
      v-for="type in selectedListingTypes"
      :key="`type-${type.id}`"
    >
      <span class="filter-value">{{ type.name }}</span>
      <button
        class="filter-delete icon-close"
        v-if="showClose"
        @click="deleteFilterValue('type', type.id)"
        :aria-label="`${type.name}, poista`"
      ></button>
    </div>
    <div class="active-filter" v-if="getFilterRange('year') && showFilter('year')">
      <span class="filter-value">{{ getRangeLabelText(getFilterRange('year'), '') }}</span>
      <button
        class="filter-delete icon-close"
        v-if="showClose"
        @click="resetFilterRange('year')"
        :aria-label="`${getRangeLabelText(getFilterRange('year'), '')}, poista`"
      ></button>
    </div>
    <div
      class="active-filter"
      v-if="getFilter('feature') && showFilter('feature')"
      v-for="feature in selectedExtraFeatures"
      :key="`feature-${feature.id}`"
    >
      <span class="filter-value">{{ feature.name }}</span>
      <button
        class="filter-delete icon-close"
        v-if="showClose"
        @click="deleteFilterValue('feature', feature.id)"
        :aria-label="`${feature.name}, poista`"
      ></button>
    </div>
    <div class="active-filter" v-if="getFilter('companyId') && company && showFilter('companyId')">
      <span class="filter-value">{{ company }}</span>
      <button
        class="filter-delete icon-close"
        v-if="showClose"
        @click="deleteFilterValue('companyId', companyId)"
        :aria-label="`${company}, poista`"
      ></button>
    </div>
    <div class="active-filter" v-if="getFilter('agentId') && agent && showFilter('agentId')">
      <span class="filter-value">{{ agent }}</span>
      <button
        class="filter-delete icon-close"
        v-if="showClose"
        @click="deleteFilterValue('agentId', agentId)"
        :aria-label="`${agent}, poista`"
      ></button>
    </div>
    <div class="active-filter" v-if="getFilter('showingIn') && showFilter('showingIn')">
      <span class="filter-value">{{ `Esittely ${getFilter('showingIn')} vrk sisällä` }}</span>
      <button
        class="filter-delete icon-close"
        v-if="showClose"
        @click="resetFilter('showingIn')"
        :aria-label="`Esittely ${getFilter('showingIn')} vrk sisällä, poista`"
      ></button>
    </div>
    <div class="active-filter" v-if="getFilter('siteOwnership') && showFilter('siteOwnership')">
      <span class="filter-value">
        {{ getFilter('siteOwnership') === config.SITE_OWNERSHIP.owned.id ? 'Omistustontti' : 'Vuokratontti' }}
      </span>
      <button
        class="filter-delete icon-close"
        v-if="showClose"
        @click="resetFilter('siteOwnership')"
        :aria-label="`${getFilter('siteOwnership') === config.SITE_OWNERSHIP.owned.id ? 'Omistustontti' : 'Vuokratontti'}, poista`"
      ></button>
    </div>
    <div class="active-filter" v-if="getFilter('has360') && showFilter('has360')">
      <span class="filter-value">Kotikierros-kohde</span>
      <button
        class="filter-delete icon-close"
        v-if="showClose"
        @click="resetFilter('has360')"
        :aria-label="`Kotikierros-kohde, poista`"
      ></button>
    </div>
    <div
      class="active-filter"
      v-if="getFilter('energy') && showFilter('energy')"
      v-for="energyClass in getFilter('energy')"
      :key="`energy-class-${energyClass}`"
    >
      <span class="filter-value"> Energialuokka {{ energyClass }} </span>
      <button
        class="filter-delete icon-close"
        v-if="showClose"
        @click="deleteFilterValue('energy', energyClass)"
        :aria-label="`Energialuokka ${energyClass}, poista`"
      ></button>
    </div>
  </div>
</template>

<script lang="ts">
import config from '@/config';
import logger from '@/app/logger';
import searchUtils, { regionTypeMapping } from '@/app/utils/search-utils';
import { computed, defineComponent, ref, watch } from 'vue';
import useSafeRoute from '@/app/composables/useSafeRoute';
import useLocationNamesData from '@/app/data/useLocationNamesData';
import type {
  CityNameType,
  ConditionsType,
  CountyNameType,
  DistrictNameType,
  ExtraFeaturesType,
  FiltersType,
  RegionNameType,
  StreetNameType,
} from '@/types/schemaTypes';

export interface RangeType {
  min: number | string;
  max: number | string;
  title: string;
}

export default defineComponent({
  name: 'active-filters',
  setup() {
    const regions = ref([] as RegionNameType[]);
    const counties = ref([] as CountyNameType[]);
    const cities = ref([] as CityNameType[]);
    const districts = ref([] as DistrictNameType[]);
    const streets = ref([] as StreetNameType[]);
    let locationFilters = ref({});

    const route = useSafeRoute();
    const filters = computed(() => {
      return searchUtils.getValidatedFiltersFromUrl(route.value.path, route.value.query);
    });
    locationFilters = computed(() => {
      const { cityId, countyId, regionId, streetId, districtId } = filters.value;
      return { cityId, countyId, regionId, streetId, districtId };
    });

    // Get names for locations (cities, counties, regions, streets, districts)
    const locationNamesEnabled = computed(() => Object.values(locationFilters.value).some((v) => !!v));
    const { data: locationNames } = useLocationNamesData(locationFilters, locationNamesEnabled);
    watch(
      locationNames,
      (result) => {
        if (!result) return;
        regions.value = result.filter((item) => item?.regionType === regionTypeMapping.region) as RegionNameType[];
        counties.value = result.filter((item) => item?.regionType === regionTypeMapping.county) as CountyNameType[];
        cities.value = result.filter((item) => item?.regionType === regionTypeMapping.city) as CityNameType[];
        districts.value = result.filter(
          (item) => item?.regionType === regionTypeMapping.district
        ) as DistrictNameType[];
        streets.value = result.filter((item) => item?.regionType === regionTypeMapping.street) as StreetNameType[];
      },
      { immediate: true, flush: 'sync' } // Ensure watch is triggered on server side and immediately on client side
    );
    return { filters, regions, counties, cities, districts, streets, route };
  },
  props: {
    showOnly: {
      type: String,
    },
    showClose: {
      type: Boolean,
      default: true,
      required: false,
    },
  },
  data: () => ({
    mobileLayoutMaxWidth: 1023,
    config,
    company: '',
    companyId: undefined,
    agent: '',
    agentId: undefined,
  }),
  methods: {
    getFilterRange(key: string) {
      const keyMin = `${key}Min`;
      const keyMax = `${key}Max`;
      const min = this.filters[keyMin as keyof FiltersType] as number;
      const max = this.filters[keyMax as keyof FiltersType] as number;
      if (!min && !max) return null;
      const title =
        key === 'price'
          ? 'Myyntihinta:'
          : key === 'livingArea'
            ? 'Pinta-ala:'
            : key === 'rent'
              ? 'Vuokra:'
              : key === 'siteArea'
                ? 'Tontin pinta-ala:'
                : key === 'totalArea'
                  ? 'Kokonaispinta-ala:'
                  : '';

      return {
        min: min ? Math.round(min) : 'Ei alarajaa',
        max: max ? Math.round(max) : 'Ei ylärajaa',
        title: title,
      } as RangeType;
    },
    getFilter(name: keyof FiltersType) {
      return this.filters[name];
    },
    deleteFilterValue(key: string, value: any) {
      searchUtils.removeFilterFromPath(this.$router, this.route, key, value).catch((error: unknown) => {
        logger.error('Filter removal from path failed on error.', error);
      });
    },
    getRangeLabelText(range: RangeType | null, unit: string) {
      if (!range) return '';
      return `${range.title} ${range.min} ${typeof range.min === 'number' ? unit : ''} - ${range.max} ${
        typeof range.max === 'number' ? unit : ''
      }`;
    },
    getAreaLabelText(range: RangeType | null) {
      return this.getRangeLabelText(range, 'm²');
    },
    getAreaLabelTextPlain(range: RangeType | null) {
      return this.getRangeLabelText(range, 'neliömetriä');
    },
    resetFilter(key: keyof FiltersType) {
      searchUtils.removeFilterFromPath(this.$router, this.route, key).catch((error: unknown) => {
        logger.error('Filter removal from path failed on error.', error);
      });
    },
    resetFilterRange(key: string) {
      const min = `${key}Min`;
      const max = `${key}Max`;
      searchUtils
        .removeFilterFromPath(this.$router, this.route, min)
        .then(() => {
          searchUtils.removeFilterFromPath(this.$router, this.route, max).catch((error: unknown) => {
            logger.error('Filter removal from path failed on error.', error);
          });
        })
        .catch((error: unknown) => {
          logger.error('Filter removal from path failed on error.', error);
        });
    },
    showFilter(name: string) {
      if (!this.showOnly) return true;
      return this.showOnly === name;
    },
    setCompanyFilter() {
      if (!!this.filters.companyId) {
        searchUtils
          .lookUpCompanyName(this.filters.companyId)
          .then((result) => {
            this.company = result.name;
            this.companyId = result.id;
          })
          .catch((error: unknown) => {
            logger.error('Company name lookup failed on error.', error);
          });
      }
    },
    setAgentFilter() {
      if (!!this.filters.agentId) {
        searchUtils
          .lookUpAgentName(this.filters.agentId)
          .then((result) => {
            this.agent = result.name;
            this.agentId = result.id;
          })
          .catch((error: unknown) => {
            logger.error('Agent name lookup failed on error.', error);
          });
      }
    },
    setActiveFilters() {
      this.setCompanyFilter();
      this.setAgentFilter();
    },
  },
  computed: {
    selectedListingTypes() {
      return this.filters.type
        ? this.filters.type.map((id) => ({
            id,
            name: config.LISTING_TYPES[id as keyof typeof config.LISTING_TYPES].name,
          }))
        : [];
    },
    selectedExtraFeatures() {
      return this.filters.feature
        ? this.filters.feature.map((id: ExtraFeaturesType) => ({ id, name: config.EXTRA_FEATURES[id] }))
        : null;
    },
    getPostalCodes() {
      return this.filters.postalCode || null;
    },
    selectedConditions() {
      return this.filters.condition
        ? this.filters.condition.map((id: ConditionsType) => ({
            id,
            name: config.APARTMENT_CONDITIONS[id as keyof typeof config.APARTMENT_CONDITIONS],
          }))
        : null;
    },
  },
  mounted() {
    this.setActiveFilters();
  },
});
</script>
